<script setup lang="ts">
import { computed } from 'vue'

import {
  isIncidentDeleted as _isIncidentDeleted,
  ParticipantName,
} from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import {
  useParticipantById,
  useTeamSides,
} from '@desktop/views/Relation/composables'
import {
  getIncidentsGroupRepresentativeIncident,
  IncidentsGroup,
  isIncidentsGroup,
} from '@desktop/views/Relation/Sports/configuration'

import IncidentConfirmation from './IncidentConfirmation.vue'
import IncidentContainer from './IncidentContainer.vue'
import IncidentControls from './IncidentControls.vue'
import IncidentDetails from './IncidentDetails/IncidentDetails.vue'
import IncidentEventStatus from './IncidentEventStatus.vue'
import IncidentEventTime from './IncidentEventTime.vue'
import IncidentName from './IncidentName.vue'
import IncidentResult from './IncidentResult.vue'
import IncidentTime from './IncidentTime.vue'
import { Result } from './types'

interface Props {
  item: Incident | IncidentsGroup
  result: Result | null
  incidentName: string
  shouldShowChangeOrderButton: boolean
  nextIncident: Incident | IncidentsGroup | undefined
  previousIncident: Incident | IncidentsGroup | undefined
}
const props = defineProps<Props>()

// composables
const { getIncidentTeamSide } = useTeamSides()

// computed
const representativeIncident = computed(() =>
  isIncidentsGroup(props.item)
    ? getIncidentsGroupRepresentativeIncident(props.item)
    : props.item,
)

const { participant } = useParticipantById(
  () => representativeIncident.value.participant_team_id,
)

const displayIncidents = computed(() =>
  isIncidentsGroup(props.item) ? props.item.incidents : [props.item],
)

const teamSide = computed(() =>
  getIncidentTeamSide(representativeIncident.value),
)

const isDeleted = computed(() =>
  displayIncidents.value.every((incident) => _isIncidentDeleted(incident)),
)
</script>

<template>
  <IncidentContainer
    class="@container/history-incident p-2 pb-0"
    :incident="representativeIncident"
    :teamSide
    :isDeleted
  >
    <div
      class="grid grid-cols-[3.25rem_auto_min-content] items-center gap-x-2 gap-y-1"
    >
      <!-- Row 1 -->
      <IncidentTime
        class="col-start-1 row-start-1 text-right text-xs"
        :incident="representativeIncident"
        :isDeleted
      />

      <div class="col-start-2 row-start-1 truncate">
        <ParticipantName
          v-if="participant"
          class="text-xs font-bold"
          :participant
          :isDeleted
          withTeamColors
        />
        <IncidentEventStatus
          :incident="representativeIncident"
          :isDeleted
        />
      </div>

      <IncidentControls
        class="col-start-3 row-span-2 row-start-1 flex justify-end self-start"
        :incident="representativeIncident"
        :displayIncidents
        :isDeleted
        :shouldShowChangeOrderButton
        :nextIncident
        :previousIncident
      />

      <!-- Row 2 -->
      <IncidentEventTime
        class="col-start-1 row-start-2"
        :incident="representativeIncident"
        :displayIncidents
        :isDeleted
      />

      <div class="col-span-2 col-start-2 row-start-2 flex items-center gap-x-1">
        <IncidentName
          class="text-sm font-bold"
          :incident="representativeIncident"
          :incidentName
          :isDeleted
        />
        <IncidentResult
          :incident="representativeIncident"
          :result
          :isDeleted
        />
        <IncidentConfirmation
          :incident="representativeIncident"
          :isDeleted
        />
      </div>
    </div>

    <div class="space-y-2 pb-2 empty:pb-0.5">
      <IncidentDetails
        v-for="displayIncident in displayIncidents"
        :key="displayIncident.id"
        :incident="displayIncident"
        :disabled="_isIncidentDeleted(displayIncident)"
        :teamSide
      />
    </div>
  </IncidentContainer>
</template>
